import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getSoilAnalysisInfo } from '../../../Utils/analysis/soilAnalysis';
import moment from 'moment';
import Header from '../../Shared/Header';
import Loading from '../../Shared/Loading';
import backIcon from "../../../assets/icons/backIcon.svg";
import { useSelector } from 'react-redux';
import excelPng from '../../../assets/images/excel.png';
import { exportTableToExcel } from '../../../Helpers/Excel/export';

const ViewSoilAnalysis = () => {
  const { exploitationId, analysisId } = useParams();
  const [analysisData, setAnalysisData] = useState(null);
  const [loading, setLoading] = useState(true);
  const userRole = useSelector((state) => state.user.userRole.name);
  const userInfo = useSelector((state) => state.user.userInfo);

  const userLink = () => {
        if (userRole === 'client') {
          return '/client';
        } else if (userRole === 'admin' || userRole === 'superadmin') {
          return '/admin';
        } else if (userRole === 'domain_manager') {
          return '/domain-manager';
        } else if (userRole === 'exploitation_manager') {
          return '/exploitation-manager';
        }
      };
  useEffect(() => {

    const fetchAnalysisDetails = async () => {
      try {
        const details = await getSoilAnalysisInfo(analysisId);
        setAnalysisData(details);
      } catch (error) {
        console.error('Erreur lors de la récupération des détails de l\'analyse:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalysisDetails();
  }, [analysisId]);


  const granulometryParameters = [
    { key: '', parameter: 'Texture' },
    { key: 'argile_', parameter: 'Argile' },
    { key: 'sablefin_', parameter: 'Sable fin' },
    { key: 'limonfins_', parameter: 'Limon fin' },
    { key: 'limongrossier_', parameter: 'Limon grossier' },
    { key: 'sablegrossier_', parameter: 'Sable grossier' },
    { key: 'gravier_', parameter: 'Gravier' },
    { key: 'humiditeeq_', parameter: 'Humidité équivalente' },
  ];

  const chemicalParameters = [
    { key: 'etatHumique', parameter: 'Etat humique', isHeader: true },
    { key: 'matiereorganique', parameter: 'Matière organique (%)' },
    { key: 'azotetotal', parameter: 'Azote total (N) (g/kg)' },
    { key: 'rapportCN', parameter: 'Rapport C/N' },
    { key: 'statutAcidoBasique', parameter: 'Statut Acido-Basique', isHeader: true },
    { key: 'pheau', parameter: 'pH eau' },
    { key: 'phkci', parameter: 'pH KCl' },
    { key: 'calcairetotal', parameter: 'Calcaire total (CaCO₃) (%)' },
    { key: 'calcaireactif', parameter: 'Calcaire actif (CaCO₃) (%)' },
    { key: 'cao', parameter: 'CaO (mg/kg)' },
    { key: 'teneurphosphoresoluble', parameter: 'Teneur en phosphore soluble (P₂O₅) (mg/kg)' },
    { key: 'potasse', parameter: 'Potasse (K₂O) (mg/kg)' },
    { key: 'magnesie', parameter: 'Magnésie (MgO) (mg/kg)' },
    { key: 'oligoElements', parameter: 'Oligo-éléments', isHeader: true },
    { key: 'cuivre', parameter: 'Cuivre (Cu) (mg/kg)' },
    { key: 'manganese', parameter: 'Manganèse (Mn) (mg/kg)' },
    { key: 'fer', parameter: 'Fer (Fe) (mg/kg)' },
    { key: 'zinc', parameter: 'Zinc (Zn) (mg/kg)' },
    { key: 'boresoluble', parameter: 'Bore soluble (B) (mg/kg)' },
    { key: 'autresElements', parameter: 'Autres éléments', isHeader: true },
    { key: 'ec', parameter: 'Conductivité électrique (EC) (1/5 mS/cm)' },
    { key: 'sodium', parameter: 'Sodium (Na) (mg/kg)' },
    { key: 'cec', parameter: 'CEC (Capacité d\'Échange Cationique) (meq/100g)' },
  ];

  const aqueousExtractParameters = [
    { key: 'potassium', parameter: 'Potassium (K⁺)' },
    { key: 'sodium', parameter: 'Sodium (Na⁺)' },
    { key: 'magnesium', parameter: 'Magnésium (Mg²⁺)' },
    { key: 'calcium', parameter: 'Calcium (Ca²⁺)' },
    { key: 'ammonium', parameter: 'Ammonium (N-NH₄⁺)' },
    { key: 'nitrate', parameter: 'Nitrate (NO₃⁻)' },
    { key: 'phosphore', parameter: 'Phosphore (PO₄³⁻)' },
    { key: 'chlorures', parameter: 'Chlorures (Cl⁻)' },
    { key: 'sulfates', parameter: 'Sulfates (SO₄²⁻)' },
  ];


  const handleExport = () => {
    const info = [
      'Analyse de Sol',
      `Date de réalisation: ${moment(analysisData.date_realisation).format('DD/MM/YYYY')}`,
      `Laboratoire: ${analysisData.laboratoire}`,
      `Échantillon: ${analysisData.echantillon}`,
      `Vanne: ${analysisData.vanne}`,
    ];

    const titles = ['Granulométrie', 'Paramètres chimiques', 'Extrait aqueux'];
    const tableIDs = ['granulometryTable', 'chemicalParametersTable', 'aqueousExtractTable'];
    const filename = `Analyse_Sol_${analysisId}`;
    const user = `${userInfo.first_name} ${userInfo.last_name}`;

    exportTableToExcel(info, tableIDs, filename, titles, null, user);
  };

  if (loading) return <Loading />;

  return (
    <div className="bg-white h-full">
      <Header title="Détails de l'Analyse de Sol" />
      <div className="h-[calc(100%-100px)] p-6">
        <Link
          to={`${userLink()}/exploitation/${exploitationId}/analysis/sol`}
          className="cursor-pointer flex pl-5 gap-2 mt-4"
        >
          <img src={backIcon} alt="" className="w-5" />
          <div className="font-semibold text-base text-indigo-600">Retour</div>
        </Link>

        {loading && <Loading />}

        {!analysisData && (
          <div className="text-center mt-4 font-semibold text-lg text-red-500">
            Aucune donnée trouvée
          </div>
        )}

        <div className="flex justify-end mb-4">
          <div
            className="flex gap-3 border p-2 hover:bg-green-200 rounded-md transition-all cursor-pointer"
            onClick={handleExport}
          >
            <span className="font-bold text-lg">Export :</span>
            <img
              src={excelPng}
              alt=""
              className="w-8"
            />
          </div>
        </div>

        <div className='bg-white p-6 rounded-lg shadow-md max-w-[1000px] m-auto *:text-center'>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <InfoItem label="Date de réalisation" value={moment(analysisData.date_realisation).format('DD/MM/YYYY')} />
            <InfoItem label="Laboratoire" value={analysisData.laboratoire} />
            <InfoItem label="Échantillon" value={analysisData.echantillon} />
            <InfoItem label="Vanne" value={analysisData.vanne} />
          </div>

          <h2 className="text-xl font-semibold mt-6 mb-4">Granulométrie</h2>
          <table className="w-full border-collapse border border-gray-300 mb-4" id='granulometryTable'>
            <thead>
              <tr>
                <HeaderTab text="Paramètre" />
                <HeaderTab text="Horizon 1" />
                <HeaderTab text="Horizon 2" />
              </tr>
            </thead>
            <tbody>
              {granulometryParameters.map(param => (
                <tr key={param.key}>
                  <td className="border border-gray-300 px-2 py-2">{param.parameter}</td>
                  <td className="border border-gray-300 px-2 py-2">{analysisData[`${param.key}h1`]}</td>
                  <td className="border border-gray-300 px-2 py-2">{analysisData[`${param.key}h2`]}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <h2 className="text-xl font-semibold mt-6 mb-4">Paramètres chimiques</h2>
          <table className="w-full border-collapse border border-gray-300 mb-4" id='chemicalParametersTable'>
            <thead className='table_heeader'>
              <tr>
                <th className="border border-gray-300 bg-gray-300 table_heeader px-2 py-2" rowSpan="2">Paramètre</th>
                <th className="border border-gray-300 bg-gray-300 px-2 table_heeader py-2 text-lg" colSpan="3">Horizon 1</th>
                <th className="border border-gray-300 px-2 bg-gray-300 table_heeader py-2 text-lg" colSpan="3">Horizon 2</th>
              </tr>
              <tr>
                <HeaderTab text="Min" />
                <HeaderTab text="Max" />
                <HeaderTab text="Donnée" />
                <HeaderTab text="Min" />
                <HeaderTab text="Max" />
                <HeaderTab text="Donnée" />
              </tr>
            </thead>
            <tbody className='text-center'>
              {chemicalParameters.map(param => (
                <tr key={param.key} className={(param.isHeader ? 'bg-gray-200 header_row' : '')}>
                  <td className="border border-gray-200 px-2 py-2">{param.parameter}</td>
                  {param.isHeader ? (
                    <td className="border border-gray-200 px-2 py-2" colSpan="6"></td>
                  ) : (
                    <>
                      <td className="border border-gray-300 px-2 py-2">{analysisData[`${param.key}_h1_min`]}</td>
                      <td className="border border-gray-300 px-2 py-2">{analysisData[`${param.key}_h1_max`]}</td>
                      <td className="border border-gray-300 px-2 py-2">{analysisData[`${param.key}_h1_data`]}</td>
                      <td className="border border-gray-300 px-2 py-2">{analysisData[`${param.key}_h2_min`]}</td>
                      <td className="border border-gray-300 px-2 py-2">{analysisData[`${param.key}_h2_max`]}</td>
                      <td className="border border-gray-300 px-2 py-2">{analysisData[`${param.key}_h2_data`]}</td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>

          <h2 className="text-xl font-semibold mt-6 mb-4">Extrait aqueux</h2>
          <table className="w-full border-collapse border border-gray-300 mb-4" id='aqueousExtractTable'>
            <thead>
              <tr>
                <HeaderTab text="Paramètre" />
                <HeaderTab text="Min (1/5mg/100g)" />
                <HeaderTab text="Max (1/5mg/100g)" />
                <HeaderTab text="H1 (1/5mg/100g)" />
                <HeaderTab text="H2 (1/5mg/100g)" />
              </tr>
            </thead>
            <tbody>
              {aqueousExtractParameters.map(param => (
                <tr key={param.key}>
                  <td className="border border-gray-300 px-2 py-2">{param.parameter}</td>
                  <td className="border border-gray-300 px-2 py-2">{analysisData[`${param.key}_min`]}</td>
                  <td className="border border-gray-300 px-2 py-2">{analysisData[`${param.key}_max`]}</td>
                  <td className="border border-gray-300 px-2 py-2">{analysisData[`${param.key}_h1`]}</td>
                  <td className="border border-gray-300 px-2 py-2">{analysisData[`${param.key}_h2`]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ViewSoilAnalysis;

const HeaderTab = ({ text }) => (
  <th className="border border-gray-300 bg-gray-300 table_heeader px-2 py-2">{text}</th>
);

const InfoItem = ({ label, value }) => (
  <div>
    <span className="font-semibold">{label}: </span>
    <span>{value}</span>
  </div>
);